import { Form } from "@/components/form/form";
import { FormField } from "@/components/form/form-field/form-field";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { IconButton } from "@/components/ui/icon-button";
import { toast } from "sonner";
import { useAuthForgotPassword } from "@/orval/auth";
import type { ForgotPasswordDTO } from "@/orval/model";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Mail, ArrowLeft } from "lucide-react";
import { useState } from "react";

const defaultValues: ForgotPasswordDTO = {
  email: "",
};

const ForgotPassword = () => {
  const { mutateAsync: forgotPasswordAsync, isPending } = useAuthForgotPassword();
  const navigate = useNavigate();
  const [emailSent, setEmailSent] = useState(false);

  const onSubmit = async (data: ForgotPasswordDTO) => {
    try {
      const message = await forgotPasswordAsync({ data });

      if (message) {
        toast.success(message);
        setEmailSent(true);
      }
    } catch (error) {
      console.error("Failed to send forgot password email:", error);
      toast.error("Failed to send reset email. Please try again.");
    }
  };

  if (emailSent) {
    return (
      <div className="flex items-center justify-center h-full flex-1">
        <Card className="max-w-full w-96">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="size-5" />
              Check Your Email
            </CardTitle>
            <CardDescription>We've sent password reset instructions to your email address.</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground font-semibold">
              If you don't see the email in a few minutes, check your spam folder.
            </p>
          </CardContent>
          <CardFooter>
            <IconButton
              className="w-full"
              variant="outline"
              icon={<ArrowLeft />}
              onClick={() => navigate({ to: "/login" })}
            >
              Back to Login
            </IconButton>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-full flex-1">
      <Card className="max-w-full w-96">
        <CardHeader>
          <CardTitle>Reset or Create Password</CardTitle>
          <CardDescription>
            Enter your email address and we'll send you a link to reset or create your password.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form<ForgotPasswordDTO> onSubmit={onSubmit} useFormProps={{ defaultValues }}>
            <FormField
              label="Email"
              name="email"
              inputProps={{
                placeholder: "Your email address",
                autoFocus: true,
                autoComplete: "email",
                type: "email",
              }}
              rules={{
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Please enter a valid email address",
                },
              }}
            />

            <IconButton className="w-full" type="submit" icon={<Mail />} disabled={isPending}>
              {isPending ? "Sending..." : "Send Reset Link"}
            </IconButton>
          </Form>
        </CardContent>
        <CardFooter className="flex justify-center">
          <IconButton
            icon={<ArrowLeft />}
            variant="outline"
            onClick={() => navigate({ to: "/login", search: { redirect: undefined, message: undefined } })}
            className="w-full"
          >
            Back to Login
          </IconButton>
        </CardFooter>
      </Card>
    </div>
  );
};

export const Route = createFileRoute("/_marketing/forgot-password")({
  component: ForgotPassword,
});
